/**
 * ROUTE-PATH SOURCE. `navItemsForGroup()` here is what
 * `features/estate/estate.routes.ts` and `features/platform/platform.routes.ts`
 * derive their paths from, so a route and its rail entry cannot drift.
 *
 * This is NOT the rail itself — that is `admin/config/admin-nav.ts`, a larger
 * registry with the groups, icons, search tags and action gating. Both files
 * are live and neither is a leftover; editing the wrong one is silent, because
 * each is consumed by something the other is not.
 */

/**
 * Admin rail navigation model.
 *
 * The `icon` field is the ONLY source for a rail glyph, and every name here is a
 * key in Ricoh Paper's ICON_REGISTRY (rendered via <paper-icon>). No two rail
 * items may share a glyph (docs/design/DESIGN_GUIDELINES.md § The rail).
 *
 * Structure (settled): up to three pinned cross-cutting items above a rule, then
 * groups; every group holds 2–5 items; one level of children is allowed for a
 * filtered view of the parent (e.g. Catering vendors under Service providers).
 *
 * PHASE NOTE: this is the Phase 2 seed used to build and prove the shell chrome.
 * The full turn-6b consolidation (7→5 groups, ~46→~24 destinations, vocabulary
 * moved to Setup pages, glyph-collision fixes) is Tier 2 = Phase 3 work
 * (audit.md CR-M2–M6), applied as each rail group's pages are migrated. Routes
 * here resolve to the placeholder page until their real pages land in Phase 3.
 */

export interface AdminNavItem {
  /** Stable key — used as the rail's activeKey and for analytics. */
  key: string;
  /** Rail label — sentence case, British English. */
  label: string;
  /** ICON_REGISTRY glyph name. Unique across the whole rail. */
  icon: string;
  /** Router path. Omitted on a pure parent (a parent discloses, it never navigates). */
  route?: string;
  /** One level of children — a filtered bookmark of the parent, never a second directory. */
  children?: readonly AdminNavItem[];
}

export interface AdminNavGroup {
  /** Group eyebrow heading (uppercased in the rail). */
  name: string;
  items: readonly AdminNavItem[];
}

/** Pinned cross-cutting items, above the rule. Max three. */
export const ADMIN_NAV_PINNED: readonly AdminNavItem[] = [
  { key: 'overview', label: 'Overview', icon: 'home', route: '/overview' },
  { key: 'insights', label: 'Insights', icon: 'bar-chart', route: '/insights' },
  { key: 'notifications', label: 'Notifications', icon: 'bell', route: '/notifications' },
];

/** Grouped rail destinations. */
export const ADMIN_NAV_GROUPS: readonly AdminNavGroup[] = [
  {
    name: 'Estate',
    items: [
      { key: 'locations', label: 'Locations', icon: 'location-pin', route: '/estate/locations' },
      { key: 'campuses', label: 'Campuses', icon: 'building', route: '/estate/campuses' },
      { key: 'map', label: 'Map', icon: 'map', route: '/estate/map' },
      { key: 'onboarding', label: 'Onboarding', icon: 'compass', route: '/estate/onboarding' },
    ],
  },
  {
    name: 'Workplace',
    items: [
      { key: 'spaces', label: 'Spaces', icon: 'room', route: '/workplace/spaces' },
      { key: 'signage', label: 'Signage', icon: 'monitor', route: '/workplace/signage' },
      { key: 'screens', label: 'Screens', icon: 'screen-panel', route: '/workplace/screens' },
      { key: 'services', label: 'Services', icon: 'wrench', route: '/workplace/services' },
    ],
  },
  {
    name: 'Servicing',
    items: [
      {
        key: 'service-providers',
        label: 'Service providers',
        icon: 'users',
        route: '/servicing/providers',
        children: [
          {
            key: 'catering-vendors',
            label: 'Catering vendors',
            icon: 'restaurant',
            route: '/servicing/providers/catering',
          },
        ],
      },
      { key: 'coverage', label: 'Coverage', icon: 'target', route: '/servicing/coverage' },
    ],
  },
  {
    // Settled rail groups Policies with People + Roles under a
    // single "People & policy" heading (DESIGN_GUIDELINES.md rail 6b).
    // "Effective permissions" is intentionally omitted — no page
    // exists for it yet, and the rail must never carry a dead destination.
    name: 'People & policy',
    items: [
      { key: 'people', label: 'People', icon: 'person', route: '/people/directory' },
      // Roles takes the shield-with-tick glyph (its prototype glyph); the plain
      // shield is freed for Policies below, so the two never collide.
      { key: 'roles', label: 'Roles', icon: 'shield-check', route: '/people/roles' },
    ],
  },
  {
    name: 'Platform',
    items: [
      { key: 'divisions', label: 'Divisions', icon: 'layers', route: '/platform/divisions' },
      { key: 'integrations', label: 'Integrations', icon: 'link', route: '/platform/integrations' },
      { key: 'apps', label: 'Apps', icon: 'grid', route: '/platform/apps' },
      // Was "Features" at /platform/features until 11 Sep 2026, when that page
      // was retired as a duplicate of Organisation's Capabilities tab. The name
      // follows the surviving surface.
      { key: 'capabilities-platform', label: 'Capabilities', icon: 'zap', route: '/platform/capabilities' },
      { key: 'insights-readiness', label: 'Insights readiness', icon: 'bar-chart', route: '/platform/insights-readiness' },
      { key: 'activity', label: 'Activity', icon: 'activity', route: '/platform/activity' },
    ],
  },
];

/** Flat list of every navigable item (pinned + grouped, incl. children) —
 *  convenient for active-key resolution and route generation. */
export const ADMIN_NAV_ALL: readonly AdminNavItem[] = [
  ...ADMIN_NAV_PINNED,
  ...ADMIN_NAV_GROUPS.flatMap((g) => g.items.flatMap((i) => [i, ...(i.children ?? [])])),
];

/**
 * Every navigable item in a rail group (its items plus any one-level children),
 * flattened and filtered to those carrying a `route`. Each group's route file
 * (`features/<group>/<group>.routes.ts`) builds its `Routes` array from this,
 * so a route and its rail entry are derived from the same source and cannot
 * drift. `groupName` must match an `ADMIN_NAV_GROUPS[].name` exactly; an unknown
 * name yields an empty array.
 */
export function navItemsForGroup(groupName: string): AdminNavItem[] {
  const group = ADMIN_NAV_GROUPS.find((g) => g.name === groupName);
  if (!group) return [];
  return group.items
    .flatMap((i) => [i, ...(i.children ?? [])])
    .filter((i) => !!i.route);
}

/** The nav item for a URL path — the LONGEST matching route prefix, so a child
 *  route (`/servicing/providers/catering`) resolves to the child, not its
 *  parent (`/servicing/providers`). Returns undefined when nothing matches. */
export function resolveActiveNavItem(path: string): AdminNavItem | undefined {
  const clean = path.split('?')[0].split('#')[0];
  let best: AdminNavItem | undefined;
  for (const item of ADMIN_NAV_ALL) {
    if (!item.route) continue;
    if (clean === item.route || clean.startsWith(item.route + '/') || clean.startsWith(item.route)) {
      if (!best || item.route.length > (best.route?.length ?? 0)) best = item;
    }
  }
  return best;
}
